// src/components/profilePage/ProfileContent.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LazyLoad from "@tripbuilder/image/LazyLoad";
import { formatImageUrl } from "@/lib/general";
import { Profile, SimilarUser } from "@/types/user";
import { useQuery } from "@tanstack/react-query";
import useAuthStore from "@/stores/user/authStore";
import { useToast } from "@/hooks/use-toast";
import { userService } from "@/services/userService";
import { Loader2, MapPinIcon } from "lucide-react";
import { socialService } from "@/services/socialService";
import { UserVisibleTripsResponse, VisibleTripResponse } from "@/types/social";
import {
  LockClosedIcon,
  GlobeAmericasIcon,
  UsersIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

import { AvatarImage } from "@/components/ui/DefaultAvatar";
import { LikeButton } from "@/components/AddShareLike";
import { AddButton } from "@/components/AddShareLike";
import { ShareButton } from "@/components/AddShareLike";

interface ProfileContentProps {
  profile: Profile | null;
  similarUsers: SimilarUser[];
  onFollowStatusChange?: (
    userId: string,
    isFollowing: boolean,
    isPrivate: boolean,
    traveler: SimilarUser
  ) => void;
}

const colours = ["indigo", "blue", "green", "yellow", "red"];

interface TripCardProps {
  trip: VisibleTripResponse;
  isSelectionMode?: boolean;
  selectedTrips: string[];
  onSelectTrip: (tripId: string) => void;
}

const TripCard: React.FC<TripCardProps> = ({
  trip,
  isSelectionMode,
  selectedTrips,
  onSelectTrip,
}) => {
  const navigate = useNavigate();
  const colours = ["indigo", "blue", "green", "yellow", "red"];
  const randomColour = colours[Math.floor(Math.random() * colours.length)];

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isSelectionMode) {
      onSelectTrip(trip.tripData.id);
    } else {
      navigate(`/trip-builder/${trip.tripData.id}`);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className={`w-full 2xl:w-[100%] rounded-xl overflow-hidden flex-shrink-0 hover:shadow-sm cursor-pointer transition-all duration-300 bg-white border border-slate-200 hover:border-slate-300 relative`}
    >
      {isSelectionMode && (
        <div className="absolute inset-0 z-10 transition-all duration-300 bg-white/60">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                selectedTrips.includes(trip.tripData.id)
                  ? "bg-indigo-600 scale-100 border-2 border-indigo-600"
                  : "bg-white/80 border-2 border-indigo-200 scale-90"
              }`}
            >
              <CheckIcon
                className={`w-6 h-6 transition-all duration-300 ${
                  selectedTrips.includes(trip.tripData.id)
                    ? "text-white scale-100 opacity-100"
                    : "text-neutral-200 scale-75 opacity-0"
                }`}
              />
            </div>
          </div>
        </div>
      )}

      <div className="relative">
        <div className="w-full h-48 2xl:h-56 overflow-hidden">
          {/* Privacy Badge */}
          <div className="absolute top-2 left-2 z-10">
            <div className="bg-white/80 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs 2xl:text-sm font-normal shadow-sm flex items-center space-x-1.5">
              {trip.tripInfo.privacy === "private" ? (
                <LockClosedIcon className="w-4 h-4 2xl:w-4 2xl:h-4 text-slate-600" />
              ) : trip.tripInfo.privacy === "friends" ? (
                <UsersIcon className="w-4 h-4 2xl:w-4 2xl:h-4 text-slate-600" />
              ) : (
                <GlobeAmericasIcon className="w-4 h-4 2xl:w-4 2xl:h-4 text-slate-600" />
              )}
              <span className="capitalize text-slate-700">
                {trip.tripInfo.privacy}
              </span>
            </div>
          </div>

          {/* Location Badge */}
          {trip.tripData.locations.filter((loc) => !loc.origin).length > 0 && (
            <div className="absolute top-2 right-2 z-10">
              <div className="bg-white/80 backdrop-blur-sm text-slate-700 px-2 py-1 rounded-full text-xs 2xl:text-sm font-normal shadow-sm flex items-center space-x-1.5">
                <MapPinIcon
                  className="w-4 h-4 2xl:w-4 2xl:h-4 text-orange-600"
                  strokeWidth={1.5}
                />
                <span>
                  {trip.tripData.locations.filter((loc) => !loc.origin).length}{" "}
                  {trip.tripData.locations.filter((loc) => !loc.origin)
                    .length === 1
                    ? "Location"
                    : "Locations"}
                </span>
              </div>
            </div>
          )}

          <div className="w-full h-full">
            {trip.tripInfo.images?.[0] ? (
              <LazyLoad
                src={formatImageUrl(trip.tripInfo.images[0], "medium")}
                lowResSrc={formatImageUrl(trip.tripInfo.images[0], "low")}
                alt={trip.tripData.name}
                className="w-full h-full object-cover transition duration-700 ease-in-out group-hover:scale-105"
                objectFit="cover"
              />
            ) : (
              <div
                className={`w-full h-full bg-gradient-to-br from-${randomColour}-400 to-${randomColour}-600 flex items-center justify-center`}
              >
                <span className="text-white text-xl 2xl:text-2xl font-semibold">
                  {trip.tripData.name?.[0]?.toUpperCase() || "T"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="py-2 px-3 2xl:py-3 2xl:px-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-semibold text-base 2xl:text-[16.5px] text-slate-800 line-clamp-1 flex-1">
            {trip.tripData.name}
          </h3>
        </div>

        <div className="flex items-center justify-between">
          <div
            className="flex items-center space-x-4"
            onClick={(e) => e.stopPropagation()}
          >
            <AddButton
              tripName={trip.tripData.name || "Unnamed Trip"}
              onAdd={() => {}}
              compact={true}
            />
            <ShareButton
              count={trip.engagement.share_count}
              tripId={trip.tripData.id}
              tripName={trip.tripData.name || "Unnamed Trip"}
              tripCreator={
                trip.tripInfo.trip_creator?.split(" ")[0] ?? "Anonymous"
              }
              isModalOpen={false}
              setIsModalOpen={() => {}}
              showCount={false}
              className="group text-neutral-500 hover:text-neutral-800"
            />
            <LikeButton
              count={trip.engagement.like_count}
              iconClassName="fa-lg"
              userLiked={trip.engagement.viewer_has_liked}
              isLiked={trip.engagement.viewer_has_liked}
              className="group text-neutral-500 hover:text-neutral-800 flex items-center space-x-2"
              onLike={() => {}}
              showCount
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ProfileContent: React.FC<ProfileContentProps> = ({
  profile,
  similarUsers: initialSimilarUsers,
  onFollowStatusChange,
}) => {
  const [activeTab, setActiveTab] = useState("trips");
  const [pendingFollows, setPendingFollows] = useState<Set<string>>(new Set());
  const [similarUsers, setSimilarUsers] = useState<SimilarUser[]>([]);
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { toast } = useToast();
  const userFirstName = profile?.name?.split(" ")[0];
  const userFirstNameApostrophe = userFirstName?.endsWith("s")
    ? userFirstName + "'"
    : userFirstName + "'s";

  useEffect(() => {
    setSimilarUsers(initialSimilarUsers);
  }, [initialSimilarUsers]);

  // Query for fetching trips
  const { data: userTrips, isLoading: isTripsLoading } =
    useQuery<UserVisibleTripsResponse>({
      queryKey: ["visible-trips", profile?.user_id],
      queryFn: async () => {
        if (!profile?.user_id)
          return { limit: 0, offset: 0, total: 0, trips: [] };
        return socialService.getUserVisibleTrips(profile?.user_id);
      },
      enabled: !!profile?.user_id,
    });

  const renderContent = () => {
    switch (activeTab) {
      case "trips":
        if (isTripsLoading) {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-y-auto scrollbar-hide">
              {/* Loading skeleton */}
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="animate-pulse overflow-y-auto scrollbar-hide"
                >
                  <div className="w-full h-40 bg-neutral-100 rounded-lg mb-2"></div>
                  <div className="h-4 bg-neutral-100 w-3/4 rounded mb-2"></div>
                  <div className="h-3 bg-neutral-100 w-1/2 rounded"></div>
                </div>
              ))}
            </div>
          );
        }

        if (!userTrips?.trips?.length) {
          return (
            <div className="h-full flex flex-col items-center justify-center pb-8 space-y-4">
              <p className="text-neutral-600 font-light text-center">
                {profile?.name
                  ? `${userFirstName} hasn't created any trips yet.`
                  : "No trips to show yet."}
              </p>
              <p className="text-neutral-600 font-light text-center">
                Explore more trips{" "}
                <span
                  onClick={() => navigate("/explore")}
                  className="font-semibold text-indigo-500 cursor-pointer"
                >
                  here
                </span>
              </p>
            </div>
          );
        }

        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-y-auto scrollbar-hide">
            {userTrips?.trips.map((tripResponse: VisibleTripResponse) => (
              <TripCard
                key={tripResponse.tripData.id}
                trip={tripResponse}
                selectedTrips={[]}
                onSelectTrip={() => {}}
              />
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  const handleFollow = async (traveler: SimilarUser) => {
    // Optimistically update the UI
    setPendingFollows((prev) => new Set(prev).add(traveler.user_id));

    try {
      const { success, message } = await userService.followUser(
        traveler.user_id
      );

      if (success) {
        // Update the local state
        setSimilarUsers((prev) =>
          prev.map((user) =>
            user.user_id === traveler.user_id
              ? {
                  ...user,
                  is_following: !traveler.is_private,
                  is_requested: traveler.is_private,
                }
              : user
          )
        );

        // Notify parent component about the follow status change
        if (onFollowStatusChange) {
          onFollowStatusChange(
            traveler.user_id,
            true,
            traveler.is_private,
            traveler
          );
        }

        if (traveler.is_private) {
          toast({
            title: "Follow Request Sent",
            description: `You have sent a follow request to ${traveler.name}`,
            duration: 2500,
          });
        }
      } else {
        toast({
          title: "Follow Request Failed",
          description: message,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to follow user",
        variant: "destructive",
      });
    } finally {
      setPendingFollows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(traveler.user_id);
        return newSet;
      });
    }
  };

  const handleUnfollow = async (traveler: SimilarUser) => {
    // Optimistically update the UI
    setPendingFollows((prev) => new Set(prev).add(traveler.user_id));

    try {
      const { success, message } = await userService.unfollowUser(
        traveler.user_id
      );

      if (success) {
        // Update the local state
        setSimilarUsers((prev) =>
          prev.map((user) =>
            user.user_id === traveler.user_id
              ? {
                  ...user,
                  is_following: false,
                  is_requested: false,
                }
              : user
          )
        );

        // Notify parent component about the follow status change
        if (onFollowStatusChange) {
          onFollowStatusChange(
            traveler.user_id,
            false,
            traveler.is_private,
            traveler
          );
        }
      } else {
        toast({
          title: "Unfollow Failed",
          description: message,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to unfollow user",
        variant: "destructive",
      });
    } finally {
      setPendingFollows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(traveler.user_id);
        return newSet;
      });
    }
  };

  const handleUserClick = (userId: string) => {
    if (userId === user?.user_id) {
      navigate("/account");
    } else {
      navigate(`/account/${userId}`);
    }
  };

  return (
    <div className="w-full flex rounded-md gap-4 h-[calc(100vh-1rem)] overflow-hidden">
      {/* Main Content - 70% width */}
      <div className="w-[70%] bg-white rounded-xl border-bottom border-neutral-100">
        {/* Navigation Tabs */}
        <div className="flex items-center gap-2.5 pb-2.5 border-b border-neutral-200">
          {[
            {
              id: "trips",
              label: `${userFirstNameApostrophe} Trips`,
              icon: "fa-regular fa-compass",
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm 2xl:text-[15px] font-medium rounded-lg transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? "text-indigo-700 bg-indigo-100"
                  : "text-indigo-400 bg-indigo-50 hover:bg-indigo-100"
              }`}
            >
              <i className={`${tab.icon} 2xl:text-[15px]`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="pt-4 pb-16 h-full overflow-y-auto scrollbar-hide">
          {renderContent()}
        </div>
      </div>

      {/* Similar Travelers Section - 30% width */}
      <div className="w-[30%] bg-white rounded-xl flex flex-col">
        {/* Header */}
        <div className="px-4 py-2 w-48 2xl:w-52 text-sm 2xl:text-[15px] font-medium rounded-lg transition-colors flex items-center gap-2 text-indigo-700 bg-indigo-100">
          <i className="fa-regular fa-user"></i>
          <h3>Similar Travelers</h3>
        </div>

        <div className="bg-white flex flex-col border border-neutral-200 rounded-lg mt-2 p-2 flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto scrollbar-hide rounded-2xl">
            <div className="space-y-4">
              {similarUsers.map((traveler) => (
                <div
                  key={traveler.user_id}
                  className="w-full flex flex-row justify-between items-center space-x-3 hover:bg-neutral-100 p-2 rounded-lg transition-colors"
                >
                  <div
                    className="flex flex-row items-center space-x-3 cursor-pointer"
                    onClick={() => handleUserClick(traveler.user_id)}
                  >
                    {/* Avatar */}
                    <AvatarImage
                      src={traveler.profile_image}
                      alt={traveler.name}
                      className="w-10 h-10 2xl:w-12 2xl:h-12"
                      firstName={traveler.name?.split(" ")[0]}
                      lastName={traveler.name?.split(" ")[1]}
                    />
                    {/* Traveler Info */}
                    <div className="flex flex-col items-start">
                      <p className="text-sm 2xl:text-[16px] font-medium text-neutral-800 truncate">
                        {traveler.name}
                      </p>
                      <p className="text-xs 2xl:pt-0.5 2xl:text-[13.5px] 2xl:pl-0.5 font-light text-neutral-500 truncate">
                        {traveler.username}
                      </p>
                    </div>
                  </div>

                  {/* Follow Button */}
                  {traveler.user_id !== user?.user_id && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        if (traveler.is_following || traveler.is_requested) {
                          handleUnfollow(traveler);
                        } else {
                          handleFollow(traveler);
                        }
                      }}
                      className={`rounded-lg p-2 text-xs 2xl:text-sm 2xl:px-3.5 ${
                        pendingFollows.has(traveler.user_id)
                          ? "bg-neutral-100"
                          : traveler.is_following || traveler.is_requested
                          ? "bg-white hover:bg-neutral-100"
                          : "bg-white hover:bg-neutral-100"
                      } border border-neutral-200 transition-colors duration-200 flex items-center gap-2`}
                      disabled={pendingFollows.has(traveler.user_id)}
                    >
                      {pendingFollows.has(traveler.user_id) ? (
                        <Loader2 className="h-3 w-3 animate-spin text-indigo-500" />
                      ) : traveler.is_following || traveler.is_requested ? (
                        traveler.is_requested ? (
                          "Requested"
                        ) : (
                          "Following"
                        )
                      ) : (
                        "Follow"
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileContent;
